/**
 * Hook to handle integration setup redirect.
 *
 * Determines if the user should be redirected to the integration setup page:
 * - User has security_dashboard feature (not redteam-only)
 * - User has no Logfire tokens configured
 * - User has not skipped the setup in this session
 *
 * Returns redirect state for the router to handle.
 */

import { useProviderConfigStatus } from "@/hooks/useProviderConfigStatus";

const SESSION_STORAGE_KEY = "integration_setup_skipped_session";

/**
 * Check if the setup was skipped in the current session.
 * Called synchronously during render for immediate reactivity.
 */
function wasSkippedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";
  } catch {
    // sessionStorage might not be available (e.g., in private browsing mode)
    return false;
  }
}

interface IntegrationSetupRedirectState {
  /** Whether to redirect to setup page */
  shouldRedirectToSetup: boolean;
  /** Whether the required data is still loading */
  isLoading: boolean;
}

export function useIntegrationSetupRedirect(): IntegrationSetupRedirectState {
  const { isLoading, needsIntegrationSetup } = useProviderConfigStatus();

  // Read sessionStorage synchronously on every render
  // This ensures we get the latest value immediately after skip is clicked
  const skippedThisSession = wasSkippedThisSession();

  // Redirect to setup if:
  // 1. Not loading
  // 2. User needs integration setup (has security_dashboard, no tokens)
  // 3. User hasn't skipped in this session
  const shouldRedirectToSetup =
    !isLoading && needsIntegrationSetup && !skippedThisSession;

  return {
    shouldRedirectToSetup,
    isLoading,
  };
}
